import { Icon } from '@iconify/react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

// card variants - based on border, color
const CARD_BORDER_VARIANTS = {
  DEFAULT: 'default',
  OUTLINED: 'outlined',
};

const CARD_COLOR_VARIANTS = {
  DEFAULT: 'default',
  PRIMARY: 'primary',
  DISABLED: 'disabled',
};

const CARD_COLOR_VARIANTS_MAP = {
  [CARD_COLOR_VARIANTS.DEFAULT]: 'background.default',
  [CARD_COLOR_VARIANTS.PRIMARY]: 'header.main',
  [CARD_COLOR_VARIANTS.DISABLED]: 'grey.400',
};

/**
 * CollapsibleCard component
 * @param {Object} props
 * @param {React.ReactNode | string} [props.title]
 * @param {React.ReactNode | string} [props.meta]
 * @param {boolean} [props.defaultExpanded=false]
 * @param {React.ReactNode} props.children
 * @param {Object} [props.sx]
 * @param {'default' | 'outlined'} [props.borderVariant='default']
 * @param {'default' | 'primary'} [props.colorVariant='primary']
 * @param {boolean} [props.contentHasBackgroundColor=false]
 * @returns {JSX.Element}
 */
export default function CollapsibleCard({
  title,
  meta,
  defaultExpanded = false,
  children,
  sx = {},
  borderVariant = 'default',
  colorVariant = 'primary',
  contentHasBackgroundColor = false,
}: {
  title?: any;
  meta?: any;
  defaultExpanded?: boolean;
  children?: any;
  sx?: any;
  borderVariant?: string;
  colorVariant?: string;
  contentHasBackgroundColor?: boolean;
  [key: string]: any;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggle = () => setExpanded((prev) => !prev);

  return (
    <Card
      variant='outlined'
      sx={{
        backgroundColor: CARD_COLOR_VARIANTS_MAP[colorVariant] || 'header.main',
        border: borderVariant === CARD_BORDER_VARIANTS.OUTLINED ? '1px solid' : 'none',
        borderColor: borderVariant === CARD_BORDER_VARIANTS.OUTLINED ? 'grey.300' : 'transparent',
        borderRadius: borderVariant === CARD_BORDER_VARIANTS.OUTLINED ? 2 : 0,
        ...sx,
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        gap={2}
        sx={{ cursor: 'pointer', padding: 2 }}
        onClick={toggle}
      >
        <Stack direction='row' gap={1} justifyContent='space-between' flex={1}>
          <div>
            {title &&
              (typeof title === 'string' ? (
                <Typography fontWeight={600}>{title}</Typography>
              ) : (
                title
              ))}
          </div>

          {meta &&
            (typeof meta === 'string' ? (
              <Typography variant='body2' color='text.secondary'>
                {meta}
              </Typography>
            ) : (
              meta
            ))}
        </Stack>

        <Icon
          icon='material-symbols:expand-more'
          width={22}
          style={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Stack>
      <Collapse
        in={expanded}
        unmountOnExit
        sx={{
          backgroundColor: contentHasBackgroundColor
            ? CARD_COLOR_VARIANTS_MAP[colorVariant] || 'header.main'
            : 'background.default',
        }}
      >
        <CardContent>{children}</CardContent>
      </Collapse>
    </Card>
  );
}
